import { Box, Button, Flex, Grid, Text, VStack, useDisclosure, useToast } from "@chakra-ui/react";
import * as React from "react";
import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { dimensionsKeys, showErrorToast } from "../../../../../../utils";
import { CategoryGridHeaderRow, CategoryGridItems } from "./index";
import { Paginator } from "../../../../../../components/Paginator";
import DimensionsGridFilterBar from "../../../DimensionsGrid/DimensionsGridFilterBar";
import { DownloadSimple, FileArrowUp, PencilSimpleLine, Sparkle, UploadSimple, X } from "@phosphor-icons/react";
import { useAuth } from "../../../../../../context";
import { isAllowedToView } from "../../../../../../utils/AuthUtils";
import MODULES from "../../../../../../config/modules";
import { permissions } from "../../../../../../config/paths";
import FileDragDrop from "../../../../../../components/FileDragDrop";
import AiMappingModal from "../../../../../aiDataMapping/components/AiMappingModal";

function CategoryGrid({
  questionnaireId,
  columnHeaders,
  attributesOrder,
  prepareSectionValues,
  handleAddItem,
  handleEditItem,
  handleDeleteItem,
  getItemsFunction,
  isPreview,
  // Legacy props from older callers — kept to avoid touching 6 category files.
  // eslint-disable-next-line no-unused-vars
  maxHeight,
  // eslint-disable-next-line no-unused-vars
  showNoDataLabel = false,
  onDownloadTemplate,
  importBtnId,
  onImport,
  organizationStructure,
  // The AI ingest destination for this category, or null/undefined when it has no backend handler yet —
  // in which case no button renders. Resolved by the caller via targetForCategory, not derived here: this
  // grid is shared by every category and does not know which one it is showing.
  aiTarget,
  // Optional total row over the primary numeric column. Mirrors the Scope 1/2
  // footer in DimensionsGrid: { valueKey, unitKey? }. When unitKey is set,
  // totals are grouped per distinct unit label (e.g. tonne-km vs km).
  totalsConfig,
}) {
  // ESG-3894: when organizationStructure is supplied, prepend one column per active
  // dimension (matches the Scope 1/2 pattern).
  const { userPermissions } = useAuth();
  // Same gate as DimensionsGrid, and it must stay the same: run_python still executes user code on a bare
  // thread in a service that shares the main app's DB credentials (ESG-3975, still unsandboxed), but access
  // is now controlled by the org-level `DataMapping` module flag rather than the ESG_ADMIN role — a
  // deliberate choice to gate by org instead of by user within an enabled org. A category-specific
  // relaxation here would widen it silently either way.
  const canUseAiMapping = Boolean(aiTarget)
    && isAllowedToView(userPermissions, permissions.aiDataMapping, [MODULES.DataMapping.id]);
  const {
    isOpen: isAiMappingOpen,
    onOpen: onAiMappingOpen,
    onClose: onAiMappingClose,
  } = useDisclosure();

  const activeDimensionKeys = organizationStructure
    ? dimensionsKeys.filter(dimKey => organizationStructure.dimensions[dimKey])
    : [];
  const dimensionHeaders = activeDimensionKeys.map(dimKey => organizationStructure.dimensions[dimKey].name);
  const mergedColumnHeaders = [...dimensionHeaders, ...columnHeaders];
  const mergedAttributesOrder = [...activeDimensionKeys, ...attributesOrder];
  const { t } = useTranslation("translation");
  const toast = useToast();

  const [itemsList, setItemsList] = useState([]);
  // Client-side pagination over the filtered list. We fetch the full dataset
  // once (size=9999) so every filter can see every row, not just the page
  // the server happened to return.
  const [pageIndex, setPageIndex] = useState(0);
  const [queryPageSize, setQueryPageSize] = useState(10);
  const [filters, setFilters] = useState({});

  const [activeMethod, setActiveMethod] = useState("direct");
  const [templateFile, setTemplateFile] = useState(null);
  const fileInputRef = useRef(null);

  getItemsFunction(
    {
      queryParams: {
        page: 0,
        size: 9999,
        sort: "id,desc",
      },
      questionnaireId: questionnaireId,
    },
    {
      onSuccess: (page) => {
        const pageContent = page?.content ?? [];
        const items = prepareSectionValues(pageContent);
        setItemsList(items);
      },
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0,
    }
  );

  const getDimensionLabel = useCallback(
    (dimKey, id) => organizationStructure?.dimensions?.[dimKey]?.items?.find(el => el.id?.toString() === id?.toString())?.name ?? "",
    [organizationStructure]
  );

  // Annotate each row with display labels per column, so the filter bar can
  // compare like-for-like and the count chip stays in sync with what the user
  // sees on the row.
  const decoratedItems = useMemo(() => itemsList.map((item) => {
    const decorated = { ...item };
    activeDimensionKeys.forEach((dimKey) => {
      decorated[`__label_${dimKey}`] = getDimensionLabel(dimKey, item[`${dimKey}Item`]);
    });
    attributesOrder.forEach((attr) => {
      const v = item[attr];
      decorated[`__label_${attr}`] = v == null || v === "" ? "" : String(v);
    });
    return decorated;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [itemsList, organizationStructure]);

  // Build dimension groups for the rolled-up Dimension pill.
  const dimensionGroups = useMemo(() => {
    const groups = [];
    activeDimensionKeys.forEach((dimKey) => {
      const labels = new Set();
      decoratedItems.forEach((row) => {
        const label = row[`__label_${dimKey}`];
        if (label) labels.add(label);
      });
      const items = [...labels].sort().map((label) => ({ label, value: label }));
      if (items.length > 0) {
        groups.push({
          dimKey,
          name: organizationStructure.dimensions[dimKey].name,
          items,
        });
      }
    });
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decoratedItems, organizationStructure]);

  // Non-dimension column pills — driven by whatever distinct labels appear in
  // the dataset. Skip columns whose values look numeric (filtering by exact
  // numeric values doesn't help discovery).
  const extraFilterColumns = useMemo(() => {
    return attributesOrder
      .map((attr, idx) => {
        const labels = new Set();
        let allNumeric = true;
        decoratedItems.forEach((row) => {
          const v = row[`__label_${attr}`];
          if (!v) return;
          labels.add(v);
          if (Number.isNaN(Number(v))) allNumeric = false;
        });
        if (labels.size === 0) return null;
        if (allNumeric && labels.size > 4) return null;
        const options = [...labels].sort().map((v) => ({ label: v, value: v }));
        return {
          key: attr,
          label: columnHeaders[idx] ?? attr,
          options,
        };
      })
      .filter(Boolean);
  }, [decoratedItems, attributesOrder, columnHeaders]);

  const filteredItems = useMemo(() => {
    const dimensionSelections = filters._dimension || [];
    const activeExtras = Object.entries(filters).filter(
      ([key, vals]) => key !== "_dimension" && Array.isArray(vals) && vals.length > 0
    );
    if (dimensionSelections.length === 0 && activeExtras.length === 0) return decoratedItems;

    return decoratedItems.filter((row) => {
      if (dimensionSelections.length > 0) {
        const byDim = dimensionSelections.reduce((acc, sel) => {
          (acc[sel.dimKey] ||= []).push(sel.label);
          return acc;
        }, {});
        for (const [dimKey, labels] of Object.entries(byDim)) {
          const rowLabel = row[`__label_${dimKey}`];
          if (!labels.includes(rowLabel)) return false;
        }
      }

      for (const [key, vals] of activeExtras) {
        const rowLabel = row[`__label_${key}`];
        if (!vals.some((v) => v.value === rowLabel)) return false;
      }

      return true;
    });
  }, [decoratedItems, filters]);

  // Sum the configured value column over the filtered set. When unitKey is
  // provided, mixing units in a single total would be meaningless, so we
  // expose one sub-total per distinct unit label.
  const filteredTotals = useMemo(() => {
    if (!totalsConfig?.valueKey) return [];
    const byUnit = new Map();
    filteredItems.forEach((row) => {
      const n = Number(row[totalsConfig.valueKey]);
      if (!Number.isFinite(n)) return;
      const unit = totalsConfig.unitKey ? (row[totalsConfig.unitKey] || "") : "";
      byUnit.set(unit, (byUnit.get(unit) || 0) + n);
    });
    return [...byUnit.entries()]
      .map(([unit, value]) => ({ unit, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredItems, totalsConfig]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / queryPageSize));
  const safePageIndex = Math.min(pageIndex, totalPages - 1);
  const pageSlice = useMemo(
    () => filteredItems.slice(safePageIndex * queryPageSize, (safePageIndex + 1) * queryPageSize),
    [filteredItems, safePageIndex, queryPageSize]
  );

  const handlePageSelection = (newPage) => setPageIndex(newPage - 1);
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPageIndex(0);
  };
  const clearAllFilters = () => { setFilters({}); setPageIndex(0); };

  return (
    <Fragment>
      {!isPreview && (
        <Flex gap={2} mb={4} align="center" flexWrap="wrap">
          <Button
            size="sm"
            variant="outline"
            borderRadius="full"
            borderColor={activeMethod === "direct" ? "lime" : "limeShade.600"}
            bg={activeMethod === "direct" ? "lightLime" : "transparent"}
            color="limeShade.600"
            leftIcon={<PencilSimpleLine size={14} />}
            onClick={() => { setActiveMethod("direct"); handleAddItem(); }}
            _hover={{ bg: "lightLime", borderColor: "lime" }}
          >
            + {t("buttons.addNewItems")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            borderRadius="full"
            borderColor={activeMethod === "import" ? "lime" : "limeShade.600"}
            bg={activeMethod === "import" ? "lightLime" : "transparent"}
            color="limeShade.600"
            leftIcon={<FileArrowUp size={14} />}
            onClick={() => setActiveMethod(activeMethod === "import" ? "direct" : "import")}
            _hover={{ bg: "lightLime", borderColor: "lime" }}
          >
            Import from Template
          </Button>
          {canUseAiMapping && (
            <Button
              size="sm"
              variant="outline"
              borderRadius="full"
              borderColor="limeShade.600"
              bg="transparent"
              color="limeShade.600"
              leftIcon={<Sparkle size={14} />}
              onClick={onAiMappingOpen}
              _hover={{ bg: "lightLime", borderColor: "lime" }}
            >
              AI Mapping
            </Button>
          )}
        </Flex>
      )}
      {activeMethod === "import" && !isPreview && (
        <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={6} mb={4} bg="gray.50" position="relative">
          <Flex justify="space-between" align="flex-start" mb={4} gap={2}>
            <Box>
              <Text fontSize="14px" fontWeight="600" color="gray.700">Import from Template</Text>
              <Text fontSize="13px" color="gray.500" mt={1}>Download a template, fill in your data, then drag and drop or select the file to import.</Text>
            </Box>
            <Box
              as="button"
              aria-label="Cancel import"
              color="gray.400"
              _hover={{ color: "gray.700" }}
              onClick={() => { setActiveMethod("direct"); setTemplateFile(null); }}
              mt={-1}
            >
              <X size={18} />
            </Box>
          </Flex>
          <FileDragDrop onFiles={(files) => { if (files?.[0]) setTemplateFile(files[0]); }} borderRadius="lg">
            <VStack
              border="2px dashed"
              borderColor={templateFile ? "lime" : "gray.300"}
              borderRadius="lg"
              py={6}
              px={4}
              spacing={2}
              cursor="pointer"
              bg={templateFile ? "lightLime" : "white"}
              _hover={{ borderColor: "lime", bg: "lightLime" }}
              transition="all 0.15s"
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadSimple size={28} color={templateFile ? "#85C22A" : "#A0AEC0"} />
              {templateFile ? (
                <Text fontSize="13px" fontWeight="600" color="gray.700">{templateFile.name}</Text>
              ) : (
                <>
                  <Text fontSize="13px" fontWeight="600" color="gray.600">Drop file here or click to browse</Text>
                  <Text fontSize="11px" color="gray.400">.xlsx, .xls</Text>
                </>
              )}
            </VStack>
          </FileDragDrop>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".xlsx,.xls"
            onChange={(e) => {
              if (e.target.files?.[0]) setTemplateFile(e.target.files[0]);
              e.target.value = null;
            }}
          />
          <Flex justify="space-between" align="center" mt={3}>
            {onDownloadTemplate ? (
              <Button
                size="sm"
                variant="outline"
                borderRadius="full"
                borderColor="limeShade.600"
                color="limeShade.600"
                leftIcon={<DownloadSimple size={14} />}
                onClick={onDownloadTemplate}
              >
                Download Template
              </Button>
            ) : <Box />}
            {templateFile && (
              <Flex gap={2}>
                <Button
                  size="sm"
                  variant="ghost"
                  color="gray.400"
                  onClick={() => setTemplateFile(null)}
                  _hover={{ color: "gray.600" }}
                >
                  Remove
                </Button>
                <Button
                  size="sm"
                  bg="lime"
                  color="pureBlack"
                  borderRadius="full"
                  leftIcon={<UploadSimple size={14} />}
                  _hover={{ bg: "limeShade.300" }}
                  onClick={() => {
                    if (onImport) {
                      const fakeEvent = { preventDefault: () => {}, target: { files: [templateFile] } };
                      onImport(fakeEvent, importBtnId);
                    }
                    setTemplateFile(null);
                  }}
                >
                  Import
                </Button>
              </Flex>
            )}
          </Flex>
        </Box>
      )}
      {canUseAiMapping && (
        <AiMappingModal
          isOpen={isAiMappingOpen}
          onClose={onAiMappingClose}
          questionnaireId={questionnaireId}
          target={aiTarget}
        />
      )}

      {itemsList.length > 0 && (
        <DimensionsGridFilterBar
          dimensionGroups={dimensionGroups}
          columns={extraFilterColumns}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearAll={clearAllFilters}
          filteredCount={filteredItems.length}
          totalCount={itemsList.length}
        />
      )}

      {itemsList.length > 0 && (
        <Box borderWidth="1px" borderRadius="md" overflow="hidden" bg="white" mb={4}>
          <Grid
            templateColumns={`repeat(${mergedAttributesOrder.length * 2 + (!isPreview ? 1 : 0)}, 1fr)`}
            minWidth="700"
          >
            <CategoryGridHeaderRow
              isPreview={isPreview}
              columnHeaders={!isPreview ? [...mergedColumnHeaders, ""] : mergedColumnHeaders}
            />
            <CategoryGridItems
              items={pageSlice}
              attributesOrder={mergedAttributesOrder}
              handleEditItem={handleEditItem}
              handleDeleteItem={handleDeleteItem}
              isPreview={isPreview}
              organizationStructure={organizationStructure}
              dimensionKeys={activeDimensionKeys}
            />
            {filteredTotals.length > 0 && filteredTotals.map((row, idx) => (
              <Box
                key={`total-${row.unit}-${idx}`}
                gridColumn={`1 / -1`}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={4}
                py={2}
                bg="lightLime"
                borderTop={idx === 0 ? "2px solid" : "1px solid"}
                borderColor="gray.200"
              >
                <Text fontSize="13px" fontWeight="600" color="gray.600">
                  {idx === 0
                    ? (filteredItems.length === itemsList.length ? "Total" : "Filtered total")
                    : ""}
                </Text>
                <Text fontSize="13px" fontWeight="700" color="gray.800">
                  {row.value.toLocaleString("en-GB", { maximumFractionDigits: 2 })}
                  {row.unit ? ` ${row.unit}` : ""}
                </Text>
              </Box>
            ))}
          </Grid>
        </Box>
      )}

      {itemsList.length === 0 && (
        <Box
          border="1px dashed"
          borderColor="gray.300"
          borderRadius="md"
          py={8}
          mb={4}
          textAlign="center"
        >
          <Text fontSize="14px" color="gray.400">
            No activity data added yet.{!isPreview && " Click \"+ Add New Items\" to get started."}
          </Text>
        </Box>
      )}

      {itemsList.length > 0 && filteredItems.length === 0 && (
        <Box
          border="1px dashed"
          borderColor="gray.300"
          borderRadius="md"
          py={8}
          mb={4}
          textAlign="center"
        >
          <Text fontSize="14px" color="gray.400">
            No items match the current filters.
          </Text>
        </Box>
      )}

      {filteredItems.length > 0 && (
        <Flex justifyContent={isPreview ? "center" : "start"} mb={4}>
          <Paginator
            selectedPage={safePageIndex + 1}
            numberOfPages={totalPages}
            handlePageSelection={(newPage) => handlePageSelection(newPage)}
            pageSize={queryPageSize}
            onPageSizeChange={(newSize) => { setQueryPageSize(newSize); setPageIndex(0); }}
            totalCount={filteredItems.length}
          />
        </Flex>
      )}
    </Fragment>
  );
}

export default CategoryGrid;
